import {
  Button,
  HSpacer,
  InfoTooltip,
  Text,
  TextLink,
  Toolbar,
  VSpacer,
} from '@/components/DesignSystem';
import {
  Filter,
  FilterOption,
  FilterSelections,
} from '@/components/DesignSystem/Toolbar/interfaces';
import { TruncatedHeaderText } from '@/components/shared/TruncatedHeaderText';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { QueryKeys } from '@/constants/QueryKeys';
import { useGetRetailerLocations } from '@/hooks/useRetailerLocations';
import { useSearch } from '@/hooks/useSearch';
import { LocationsDialog } from '@/pages/RetailerOverview/PointsOverview/LocationsDialog';
import {
  PurchaseCategoryFilterChip,
} from '@/pages/RetailerOverview/PointsOverview/PurchaseCategoryFilterChip';
import {
  ProgramPeriodFilterChip,
} from '@/pages/RetailerOverview/ProgramPeriodFilterChip';
import { SalespeopleDialog } from '@/pages/RetailerOverview/PointsOverview/SalespeopleDialog';
import {
  ViewCustomerDetailsFullScreenModal,
} from '@/pages/RetailerOverview/ViewCustomerDetailsFullScreenModal';
import { ExportReportDialog } from '@/pages/Reports/ExportReportDialog';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { RetailerEndpoint } from '@api/endpoints/RetailerEndpoint';
import { ApiRetailer, ApiRetailerLocation, ApiUser } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReportType, UserType } from '@shared/enums';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { SharedConfig } from '@shared/SharedConfig';
import { formatNumber, getAccountName, getProgramStatus, getTierName } from '@shared/utilities';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery } from 'react-query';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface CustomerOverviewReportProps {
  onCloseExportDialog: () => void,
  onProgramChange?: (program: ApiRewardsProgram) => void,
  program: ApiRewardsProgram,
  retailer: ApiRetailer,
  showExportConfirmationDialog: boolean,
}

export const CustomerOverviewReport = ({
  onCloseExportDialog,
  onProgramChange,
  program,
  retailer,
  showExportConfirmationDialog,
}: CustomerOverviewReportProps) => {
  const { t, i18n } = useTranslation();
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => new Map());
  const [purchaseCategoryIds, setPurchaseCategoryIds] = useState<string[]>([]);
  const [hasPurchasedCategory, setHasPurchasedCategory] = useState<boolean | undefined>(undefined);
  const [sortBy, setSortBy] = (
    useState<RetailerEndpoint.GetCustomerOverview.CustomerOverviewSort>('rankCurrent')
  );
  const [sortDesc, setSortDesc] = useState(true);
  const [selectedViewAccountCustomer, setSelectedViewAccountCustomer] = useState<ApiUser>();
  const [customerLocationsToDisplay, setCustomerLocationsToDisplay] = (
    useState<{ accountGroupId: string, customer: ApiUser, locations: ApiRetailerLocation[] }>()
  );
  const [customerSalespeopleToDisplay, setCustomerSalespeopleToDisplay] = (
    useState<{ accountGroupId: string, customer: ApiUser, salespeople: ApiUser[] }>()
  );
  const tableAnchorRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!tableAnchorRef.current) return;
      const { top } = tableAnchorRef.current.getBoundingClientRect();
      setTableHeight(window.innerHeight - top - 64);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (tableAnchorRef.current?.parentElement) {
      observer.observe(tableAnchorRef.current.parentElement);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const {
    search: customerSearch,
    debouncedSearch: debouncedCustomerSearch,
    setSearch: setCustomerSearch,
  } = useSearch();
  const { retailerLocations } = useGetRetailerLocations(
    retailer.id,
    { limit: SharedConfig.maxPageLimit },
  );

  const locationFilterOptions: FilterOption[] = retailerLocations?.data
    .map((location) => ({
      id: location.id,
      label: location.name,
    })) ?? [];

  const stringifiedFilterSelections = JSON.stringify(
    Array.from(filterSelections).map(([, values]) => Array.from(values)),
  );

  const handleSort = (
    newSortBy: RetailerEndpoint.GetCustomerOverview.CustomerOverviewSort,
  ) => {
    const isSortCurrentlySelected = newSortBy === sortBy;
    if (isSortCurrentlySelected) {
      if (newSortBy === 'rankCurrent') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy('rankCurrent');
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(newSortBy);
      setSortDesc(true);
    }
  };

  const { data: filterOptions } = useQuery(
    [QueryKeys.GET_POINTS_OVERVIEW_FILTER_OPTIONS, retailer.id, program.id],
    () => RetailerApi.getPointsOverviewFilterOptions(retailer.id, { programId: program.id }),
  );

  const {
    data: primaryCustomers,
    fetchNextPage: fetchNextPrimaryCustomersPage,
    hasNextPage: hasNextPrimaryCustomersPage,
    isFetching: isFetchingNextPrimaryCustomersPage,
  } = useInfiniteQuery(
    [QueryKeys.LIST_USERS, retailer.id, debouncedCustomerSearch],
    async ({ pageParam = 0 }) => (
      UserApi.list({
        limit: 20,
        page: pageParam,
        retailerId: retailer.id,
        search: debouncedCustomerSearch,
        userType: [UserType.Farmer],
      })
    ),
    {
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const queryBody: Omit<RetailerEndpoint.GetCustomerOverview.Request, 'limit' | 'page'> = useMemo(
    () => {
      const selectedCustomers = Array.from(filterSelections?.get('customer') ?? []);
      const selectedSalespersons = Array.from(filterSelections?.get('salesperson') ?? []);
      const selectedLocations = Array.from(filterSelections?.get('location') ?? []);

      return {
        ...(selectedCustomers.length ? { id: selectedCustomers } : {}),
        ...(selectedSalespersons.length ? { salespersonId: selectedSalespersons } : {}),
        ...(selectedLocations.length ? { locationId: selectedLocations } : {}),
        programId: program.id,
        ...(hasPurchasedCategory === true ? { hasPurchasedCategoryId: purchaseCategoryIds } : {}),
        ...(hasPurchasedCategory === false
          ? { hasNotPurchasedCategoryId: purchaseCategoryIds }
          : {}
        ),
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stringifiedFilterSelections, purchaseCategoryIds, hasPurchasedCategory, program.id],
  );

  const {
    data: customers,
    fetchNextPage: fetchNextCustomersPage,
    hasNextPage: hasNextCustomersPage,
    isFetchingNextPage: isFetchingNextCustomersPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.LIST_CUSTOMER_OVERVIEW, retailer.id, queryBody, sortBy, sortDesc],
    async ({ pageParam = 0 }) => (
      RetailerApi.listCustomerOverview(
        retailer.id,
        {
          limit: 20,
          page: pageParam,
          sort: sortBy,
          sortDesc,
          ...queryBody,
        },
      )
    ),
    {
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page ? lastPage.page + 1 : undefined
      ),
    },
  );

  const filterByProgram = getProgramStatus(program) !== ProgramStatus.Active;
  const programAsterisk = filterByProgram ? ' *' : '';

  const subwalletColumns = customers?.pages?.[0]?.data?.[0]?.subwalletPoints ?? [];
  // Retailer-level flag carried on every row, so the column does not come and go with the page.
  const hasLockedProgramYear = (
    customers?.pages?.[0]?.data?.[0]?.hasLockedProgramYear ?? false
  );

  const hasPurchaseCategorySelections = Array.from(filterSelections.values())
    .some((s) => s.size > 0);

  const filters: Filter[] = useMemo(() => [
    {
      id: 'customer',
      infiniteScrollOptions: {
        fetchMoreItems: fetchNextPrimaryCustomersPage,
        hasMore: hasNextPrimaryCustomersPage ?? true,
        isLoading: isFetchingNextPrimaryCustomersPage,
      },
      isChipLabelVerbose: true,
      label: t($ => $.common.customer),
      onSearch: (search) => setCustomerSearch(search),
      options: (primaryCustomers?.pages?.flatMap(({ data }) => data) ?? []).map((customer) => ({
        id: customer.id,
        label: getAccountName(customer),
      })),
      search: customerSearch,
      selectionMethod: 'multi-select',
    },
    {
      id: 'salesperson',
      isChipLabelVerbose: true,
      label: t($ => $.overview.tierProgress.salesperson),
      options: filterOptions?.salespersons ?? [],
      selectionMethod: 'multi-select',
    },
    {
      id: 'location',
      isChipLabelVerbose: true,
      label: t($ => $.overview.tierProgress.location),
      options: locationFilterOptions,
      selectionMethod: 'multi-select',
    },
  ], [
    t,
    customerSearch,
    fetchNextPrimaryCustomersPage,
    filterOptions,
    hasNextPrimaryCustomersPage,
    isFetchingNextPrimaryCustomersPage,
    locationFilterOptions,
    primaryCustomers,
    setCustomerSearch,
  ]);

  const getTierProgressStatusLabel = (
    status: 'earnedNewTier' | 'reearnedTier' | 'tierNotReearned',
  ) => t($ => $.reports.reportType.customerOverview.tierProgressStatus[status]);

  const renderTableData = () => {
    if (isLoading) {
      return null;
    }
    return (
      <>
        {(customers?.pages?.flatMap(({ data }) => data) ?? []).map((row) => {
          const { customer, linkedAccounts, locations, salespersons } = row;
          const walletAccounts = [customer, ...linkedAccounts];
          return (
            <TableRow key={customer.id}>
              <TableCell
                height={67}
                sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}
                width={130}
              >
                {[customer, ...linkedAccounts].map((a) => a.externalId).join(',\n')}
              </TableCell>
              <TableCell height={67} sx={{ overflowWrap: 'anywhere' }} width={180}>
                {row.linkedAccountGroupId || t($ => $.common.none)}
              </TableCell>
              <TableCell
                height={67}
                sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}
                width={220}
              >
                {walletAccounts.map((account) => (
                  getAccountName(account, { appendBusinessName: true })),
                ).join(',\n')}
              </TableCell>
              <TableCell height={67} width={130}>
                {row.accountType
                  ? row.accountType.replace(/\b\w/g, (c) => c.toUpperCase())
                  : t($ => $.common.status.none)}
              </TableCell>
              <TableCell height={67} width={130}>
                {!salespersons?.length
                  ? t($ => $.common.none)
                  : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setCustomerSalespeopleToDisplay({
                          accountGroupId: row.linkedAccountGroupId,
                          customer,
                          salespeople: salespersons,
                        });
                      }}
                      testID={`${customer.id}-salespeople-button`}
                    >
                      {t($ => $.common.units.salespeopleCount, { count: salespersons.length })}
                    </TextLink>
                  )
                }
              </TableCell>
              <TableCell height={67} width={115}>
                {!locations?.length
                  ? t($ => $.common.none)
                  : (
                    <TextLink
                      onClick={(e) => {
                        e.preventDefault();
                        setCustomerLocationsToDisplay({
                          accountGroupId: row.linkedAccountGroupId,
                          customer,
                          locations,
                        });
                      }}
                      testID={`${customer.id}-locations-button`}
                    >
                      {t($ => $.common.units.locationsCount, { count: locations.length })}
                    </TextLink>
                  )
                }
              </TableCell>
              <TableCell height={67} width={150}>
                {getTierName(program.tiers ?? [], row.rankCurrent, t($ => $.common.status.none))}
              </TableCell>
              <TableCell height={67} width={150}>
                {getTierName(program.tiers ?? [], row.rankEarned, t($ => $.common.status.none))}
              </TableCell>
              <TableCell height={67} width={140}>
                {row.rankOverride !== null
                  ? getTierName(program.tiers ?? [], row.rankOverride, t($ => $.common.status.none))
                  : ''}
              </TableCell>
              <TableCell height={67} width={155}>
                {row.tierProgressStatus
                  ? getTierProgressStatusLabel(row.tierProgressStatus)
                  : ''}
              </TableCell>
              <TableCell height={67} width={130}>
                {row.nextTierRank !== null
                  ? getTierName(program.tiers ?? [], row.nextTierRank, 'N/A')
                  : 'N/A'}
              </TableCell>
              <TableCell align="right" height={67} sx={{ paddingRight: '24px' }} width={145}>
                {row.categoriesToNextTier}
              </TableCell>
              <TableCell align="right" height={67} sx={{ paddingRight: '24px' }} width={130}>
                {formatNumber(row.totalAvailablePoints, i18n)}
              </TableCell>
              <TableCell align="right" height={67} sx={{ paddingRight: '24px' }} width={130}>
                {formatNumber(row.totalReservedPoints, i18n)}
              </TableCell>
              {hasLockedProgramYear && (
                <TableCell align="right" height={67} sx={{ paddingRight: '24px' }} width={130}>
                  {formatNumber(row.totalLockedPoints, i18n)}
                </TableCell>
              )}
              <TableCell align="right" height={67} sx={{ paddingRight: '24px' }} width={130}>
                {formatNumber(row.totalRedeemedPoints, i18n)}
              </TableCell>
              {row.subwalletPoints.flatMap((sw) => [
                <TableCell
                  align="right"
                  height={67}
                  key={`${customer.id}-${sw.subwalletId}-available`}
                  sx={{ paddingRight: '24px' }}
                  width={145}
                >
                  {formatNumber(sw.available, i18n)}
                </TableCell>,
                <TableCell
                  align="right"
                  height={67}
                  key={`${customer.id}-${sw.subwalletId}-reserved`}
                  sx={{ paddingRight: '24px' }}
                  width={145}
                >
                  {formatNumber(sw.reserved, i18n)}
                </TableCell>,
                <TableCell
                  align="right"
                  height={67}
                  key={`${customer.id}-${sw.subwalletId}-redeemed`}
                  sx={{ paddingRight: '24px' }}
                  width={145}
                >
                  {formatNumber(sw.redeemed, i18n)}
                </TableCell>,
                ...(hasLockedProgramYear && sw.isProgramSubwallet ? [
                  <TableCell
                    align="right"
                    height={67}
                    key={`${customer.id}-${sw.subwalletId}-locked`}
                    sx={{ paddingRight: '24px' }}
                    width={145}
                  >
                    {formatNumber(sw.locked, i18n)}
                  </TableCell>,
                ] : []),
              ])}
              <TableCell height={67} width={132}>
                <Button
                  onClick={() => setSelectedViewAccountCustomer(customer)}
                  size="small"
                  square
                  testID={`${customer.id}-view-account-button`}
                >
                  {t($ => $.common.actions.viewAccount)}
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </>
    );
  };

  const sortLabelSx = { '&:not(.Mui-active) .MuiTableSortLabel-icon': { display: 'none' } };

  return (
    <Stack>
      <Stack alignItems="center" direction="row" flexWrap="wrap">
        <Toolbar
          filterSelections={filterSelections}
          filters={filters}
          hideSearch
          onChange={({ selections }) => {
            setFilterSelections(selections ?? new Map());
          }}
          retainSelectionsOnFilterChange
          testID="customer-overview-filters"
        />
        <PurchaseCategoryFilterChip
          hasPurchasedCategory={hasPurchasedCategory}
          onChange={(categoryIds, hasPurchased) => {
            setPurchaseCategoryIds(categoryIds);
            setHasPurchasedCategory(hasPurchased);
          }}
          options={filterOptions?.purchaseCategories ?? []}
          purchaseCategoryIds={purchaseCategoryIds}
          testID="customer-overview-purchase-category-filter-chip"
        />
        {!!onProgramChange && (
          <>
            <HSpacer size="3" />
            <ProgramPeriodFilterChip
              onSelectProgram={onProgramChange}
              programs={agronomyProgramsOnly(retailer.rewardsPrograms)}
              selectedProgram={program}
              testID="customer-overview-program-period-filter-chip"
            />
          </>
        )}
        {(hasPurchaseCategorySelections || purchaseCategoryIds.length > 0) && (
          <>
            <HSpacer size="3" />
            <TextLink
              category="title-medium"
              onClick={() => {
                setFilterSelections(new Map());
                setHasPurchasedCategory(undefined);
                setPurchaseCategoryIds([]);
              }}
              testID="customer-overview-filters-clear-all-button"
            >
              {t($ => $.common.actions.clearAll)}
            </TextLink>
          </>
        )}
      </Stack>
      <VSpacer size="3" />
      <Stack direction="row" justifyContent="space-between">
        <Text category="body-small" textAlign="left">
          {t($ => $.common.units.itemsCount, { count: customers?.pages?.[0]?.total ?? 0 })}
        </Text>
        <Text category="body-small" textAlign="right">
          {t($ => $.overview.selectedProgramPeriodOnly)}
        </Text>
      </Stack>
      <VSpacer size="3" />
      <div ref={tableAnchorRef} />
      <InfiniteScroll
        fetchMoreItems={fetchNextCustomersPage}
        hasMore={hasNextCustomersPage ?? true}
        height={tableHeight > 0 ? `${tableHeight}px` : undefined}
        isLoading={isLoading || isFetchingNextCustomersPage}
      >
        <Table
          sx={{
            '& .MuiTableCell-body': { py: '4px' },
            minWidth: 1925,
            tableLayout: 'fixed',
          }}
        >
          <TableHead
            sx={{
              '& .MuiTableCell-head': { py: '4px' },
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
            <TableRow>
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.reports.reportType.customerOverview.customerId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={180}>
                <TruncatedHeaderText>
                  {t($ => $.reports.reportType.customerOverview.linkedAccountGroupId)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={220}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('name')}
                  sx={sortLabelSx}
                >
                  <TruncatedHeaderText>
                    {t($ => $.reports.reportType.customerOverview.customerName)}
                  </TruncatedHeaderText>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.accountType)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.salesperson)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={115}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.location)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={150}>
                <TableSortLabel
                  active={sortBy === 'rankCurrent'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('rankCurrent')}
                  sx={sortLabelSx}
                >
                  <TruncatedHeaderText>
                    {t($ => $.reports.reportType.customerOverview.currentTier)}
                  </TruncatedHeaderText>
                  <HSpacer size="2" />
                  <InfoTooltip color="info">
                    {t($ => $.overview.kpis.currentStatusTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={150}>
                <TableSortLabel
                  active={sortBy === 'rankEarned'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('rankEarned')}
                  sx={sortLabelSx}
                >
                  <TruncatedHeaderText>
                    {t($ => $.overview.tierProgress.reEarnedTierWithAsterisk)}
                  </TruncatedHeaderText>
                  <HSpacer size="2" />
                  <InfoTooltip color="info">
                    {t($ => $.overview.tierProgress.reEarnedTierTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={140}>
                <TableSortLabel
                  active={sortBy === 'rankOverride'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('rankOverride')}
                  sx={sortLabelSx}
                >
                  <TruncatedHeaderText>
                    {t($ => $.overview.tierProgress.startingTier)}
                  </TruncatedHeaderText>
                </TableSortLabel>
              </TableCell>
              <TableCell height={76} variant="head" width={155}>
                <TruncatedHeaderText>
                  {t($ => $.reports.reportType.customerOverview.tierProgressHeader)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>
                  {t($ => $.overview.tierProgress.nextTier)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell align="right" height={76} variant="head" width={145}>
                <TableSortLabel
                  active={sortBy === 'categoriesToNextTier'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('categoriesToNextTier')}
                  sx={{ ...sortLabelSx, flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.tierProgress.categoriesToReachNextTierWithAsterisk)}
                    </TruncatedHeaderText>
                  </Box>
                  <HSpacer size="2" />
                  <InfoTooltip color="info">
                    {t($ => $.overview.tierProgress.categoriesToReachNextTierTooltip)}
                  </InfoTooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" height={76} variant="head" width={130}>
                <TableSortLabel
                  active={sortBy === 'totalAvailablePoints'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('totalAvailablePoints')}
                  sx={{ ...sortLabelSx, flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.availablePoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" height={76} variant="head" width={130}>
                <TableSortLabel
                  active={sortBy === 'totalReservedPoints'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('totalReservedPoints')}
                  sx={{ ...sortLabelSx, flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.reservedPoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              {hasLockedProgramYear && (
                <TableCell align="right" height={76} variant="head" width={130}>
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.wallet.locked.title)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableCell>
              )}
              <TableCell align="right" height={76} variant="head" width={130}>
                <TableSortLabel
                  active={sortBy === 'totalRedeemedPoints'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('totalRedeemedPoints')}
                  sx={{ ...sortLabelSx, flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.redeemedPoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              {subwalletColumns.flatMap((sw) => [
                <TableCell
                  align="right"
                  height={76}
                  key={`${sw.subwalletId}-available-header`}
                  variant="head"
                  width={145}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {sw.subwalletName} {t($ => $.overview.kpis.availablePoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableCell>,
                <TableCell
                  align="right"
                  height={76}
                  key={`${sw.subwalletId}-reserved-header`}
                  variant="head"
                  width={145}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {sw.subwalletName} {t($ => $.overview.kpis.reservedPoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableCell>,
                <TableCell
                  align="right"
                  height={76}
                  key={`${sw.subwalletId}-redeemed-header`}
                  variant="head"
                  width={145}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {sw.subwalletName} {t($ => $.overview.kpis.redeemedPoints)}{programAsterisk}
                    </TruncatedHeaderText>
                  </Box>
                </TableCell>,
                ...(hasLockedProgramYear && sw.isProgramSubwallet ? [
                  <TableCell
                    align="right"
                    height={76}
                    key={`${sw.subwalletId}-locked-header`}
                    variant="head"
                    width={145}
                  >
                    <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                      <TruncatedHeaderText>
                        {sw.subwalletName} {t($ => $.wallet.locked.title)}{programAsterisk}
                      </TruncatedHeaderText>
                    </Box>
                  </TableCell>,
                ] : []),
              ])}
              <TableCell height={76} variant="head" width={132} />
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableData()}
          </TableBody>
        </Table>
      </InfiniteScroll>
      {showExportConfirmationDialog && (
        <ExportReportDialog
          confirmMessage={t($ => $.overview.tierProgress.exportWarning)}
          onClose={onCloseExportDialog}
          request={{ params: queryBody, type: ReportType.CustomerOverview }}
          retailerId={retailer.id}
        />
      )}
      {!!customerLocationsToDisplay && (
        <LocationsDialog
          accountGroupId={customerLocationsToDisplay.accountGroupId || undefined}
          customer={customerLocationsToDisplay.customer}
          locations={customerLocationsToDisplay.locations}
          onClose={() => setCustomerLocationsToDisplay(undefined)}
        />
      )}
      {!!customerSalespeopleToDisplay && (
        <SalespeopleDialog
          accountGroupId={customerSalespeopleToDisplay.accountGroupId || undefined}
          customer={customerSalespeopleToDisplay.customer}
          onClose={() => setCustomerSalespeopleToDisplay(undefined)}
          salespeople={customerSalespeopleToDisplay.salespeople}
        />
      )}
      {!!selectedViewAccountCustomer && (
        <ViewCustomerDetailsFullScreenModal
          id={selectedViewAccountCustomer.id}
          onClose={() => setSelectedViewAccountCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
        />
      )}
    </Stack>
  );
};
